import type { Range } from 'quill/core/selection'
import Clipboard from 'quill/modules/clipboard'

import { normalizeCocoaHtml } from './normalize-cocoa-html'
import { normalizePastedText } from './normalizePastedText'
import { normalizeSvgTextLayer } from './normalize-svg-text-layer'
import { normalizeWordHtml } from './normalize-word-html'
import { pastedTextToHtml } from './pastedTextToHtml'

const hasMarkup = (html: string) =>
  new DOMParser().parseFromString(html, 'text/html').body.childElementCount > 0

type PastedContent = {
  text?: string
  html?: string
}

/**
 * Sources that hand over HTML (web pages, other editors) already carry their
 * own structure, so only a plain-text-only paste is normalized - except Word,
 * PDF text copied from macOS Preview and SVG document previews, whose HTML
 * keeps that structure where Quill cannot see it.
 */
export class NormalizingClipboard extends Clipboard {
  onPaste(range: Range, pasted: PastedContent) {
    const { text } = pasted
    const inCodeBlock = 'code-block' in this.quill.getFormat(range.index)

    // Chrome's PDF viewer also puts its plain text on the clipboard as HTML,
    // where the line breaks are bare newlines that HTML collapses into spaces.
    const html = pasted.html && hasMarkup(pasted.html) ? pasted.html : undefined

    if (html && !inCodeBlock) {
      const normalized =
        normalizeCocoaHtml(html) ??
        normalizeSvgTextLayer(html) ??
        normalizeWordHtml(html)

      if (normalized !== null) {
        super.onPaste(range, { text, html: normalized })
        return
      }
    }

    if (text && !html && !inCodeBlock) {
      const normalized = normalizePastedText(text)

      if (normalized !== null) {
        super.onPaste(range, {
          text: normalized,
          html: pastedTextToHtml(normalized),
        })
        return
      }
    }

    super.onPaste(range, { text, html })
  }
}
